import React, { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { MapIcon } from '@heroicons/react/24/outline';
import api from '../../api/axios';

const DashboardLayout = ({ setIsAuthenticated, onLogout }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [notifOpen, setNotifOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [user, setUser] = useState({ full_name: '', email: '' });
  const notifRef = useRef();
  const userRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      setIsAuthenticated(false);
      navigate('/login');
    }
  };

  const handleNotifClick = () => {
    setNotifOpen((open) => !open);
    setUserMenuOpen(false);
  };

  const handleUserMenuClick = () => {
    setUserMenuOpen((open) => !open);
    setNotifOpen(false);
  };

  const handleMarkAllRead = async () => {
    try {
      await api.patch('/notifications/mark-all-read');
      // Refetch notifications to update UI
      const { data } = await api.get('/notifications');
      setNotifications(data.data || data || []);
    } catch (err) {
      // fallback: mark as read locally
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    }
  };

  // Close dropdowns on outside click
  React.useEffect(() => {
    const handleClick = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
      if (userRef.current && !userRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const { data } = await api.get('/notifications');
        setNotifications(data.data || data || []);
      } catch (err) {
        setNotifications([]);
      }
    };
    fetchNotifications();
    // Poll for notifications every 20 seconds
    const interval = setInterval(fetchNotifications, 20000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Fetch user profile on mount
    const fetchUser = async () => {
      try {
        const res = await api.get('/users/profile');
        setUser(res.data || { full_name: '', email: '' });
      } catch (err) {
        setUser({ full_name: 'User', email: '' });
      }
    };
    fetchUser();
  }, []);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z' },
    { name: 'My Construction', href: '/construction', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2-2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
    { name: 'My Documents', href: '/documents', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
    { name: 'Payments', href: '/payments', icon: 'M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 10c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z' },
    { name: 'Material Catalog', href: '/materials', icon: 'M4 6h16M4 12h16M4 18h16' },
    { name: 'My Vastu', href: '/vastu', icon: 'M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 10c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z' },
    { name: 'Support & Service', href: '/support', icon: 'M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z' },
    { name: 'Notifications', href: '/notifications', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' },
    { name: 'Profile', href: '/profile', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-strong transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:relative lg:translate-x-0 lg:flex-shrink-0`}>
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="flex items-center justify-start h-16 px-6 border-b border-gray-200">
            <div className="flex items-center space-x-3 w-full min-w-0">
              <div className="w-8 h-8 bg-gradient-to-r from-palamuru-saffron to-palamuru-gold rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-display-bold text-sm">P</span>
              </div>
              <span className="font-display-bold text-lg md:text-xl text-gray-800 break-words leading-tight max-w-[10.5rem] sm:max-w-[12rem] whitespace-normal">
                Palamuru Constructions
              </span>
            </div>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 mt-8 px-4">
            <div className="space-y-2">
              {navigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    `flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-body-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-gradient-to-r from-palamuru-blue/10 to-palamuru-blue/5 text-palamuru-blue border border-palamuru-blue/20'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`
                  }
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                  </svg>
                  <span>{item.name}</span>
                </NavLink>
              ))}
            </div>
          </nav>

          {/* Logout Button */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-body-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-all duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div className="sticky top-0 z-30 bg-white shadow-soft border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-6">
            <button
              onClick={() => setIsSidebarOpen(true)}
              className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <div className="flex items-center space-x-4 ml-auto">
              {/* Notifications */}
              <div className="relative" ref={notifRef}>
                <button
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors relative"
                  onClick={handleNotifClick}
                  aria-label="Notifications"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-palamuru-blue text-white text-xs rounded-full px-1.5 py-0.5 font-body-semibold shadow">{unreadCount}</span>
                  )}
                </button>
                {notifOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 animate-fade-in">
                    <div className="flex items-center justify-between px-4 pt-4 pb-2">
                      <span className="font-display-semibold text-gray-900 text-base">Notifications</span>
                      <button className="text-xs text-palamuru-blue hover:text-palamuru-gold font-body-semibold" onClick={handleMarkAllRead}>Mark all as read</button>
                    </div>
                    <ul className="max-h-72 overflow-y-auto divide-y divide-gray-100">
                      {notifications.length === 0 ? (
                        <li className="px-4 py-6 text-center text-gray-400 font-body">No notifications</li>
                      ) : notifications.map(n => (
                        <li
                          key={n.id}
                          className={`px-4 py-3 flex flex-col gap-0.5 cursor-pointer ${!n.is_read ? 'bg-palamuru-blue/5 font-semibold text-gray-900' : 'text-gray-500'}`}
                          onClick={async () => {
                            if (!n.is_read) {
                              try {
                                await api.patch(`/notifications/${n.id}/read`);
                                // Refetch notifications to update count and style
                                const { data } = await api.get('/notifications');
                                setNotifications(data.data || data || []);
                              } catch (err) {
                                // fallback: mark as read locally
                                setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x));
                              }
                            }
                          }}
                        >
                          <span>{n.title}</span>
                          <span className="text-xs">{n.message}</span>
                          <span className="text-xs text-gray-400 mt-1">{new Date(n.created_at).toLocaleString()}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="px-4 py-3 border-t border-gray-100 text-right">
                      <button
                        className="text-sm text-palamuru-blue hover:text-palamuru-gold font-body-semibold"
                        onClick={() => { setNotifOpen(false); navigate('/notifications'); }}
                      >
                        View all notifications
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* User menu */}
              <div className="relative" ref={userRef}>
                <button
                  className="flex items-center space-x-3 focus:outline-none group"
                  onClick={handleUserMenuClick}
                  aria-label="User menu"
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-palamuru-saffron to-palamuru-gold rounded-full flex items-center justify-center">
                    <span className="text-white font-body-bold text-sm">{(user.full_name?.[0] || user.email?.[0] || 'U').toUpperCase()}</span>
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="font-body-semibold text-sm text-gray-800 group-hover:text-palamuru-blue transition-colors">{user.full_name || user.email || 'User'}</p>
                    <p className="font-body text-xs text-gray-500">Customer</p>
                  </div>
                  <svg className="w-4 h-4 text-gray-400 group-hover:text-palamuru-blue transition-colors ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 animate-fade-in">
                    <ul className="py-2">
                      <li>
                        <button className="w-full text-left px-4 py-3 font-body text-gray-700 hover:bg-gray-50 rounded-xl" onClick={() => { setUserMenuOpen(false); navigate('/profile'); }}>View Profile</button>
                      </li>
                      <li>
                        <button className="w-full text-left px-4 py-3 font-body text-gray-700 hover:bg-gray-50 rounded-xl" onClick={() => { setUserMenuOpen(false); navigate('/documents'); }}>My Documents</button>
                      </li>
                      <li>
                        <button className="w-full text-left px-4 py-3 font-body text-gray-700 hover:bg-gray-50 rounded-xl" onClick={() => { setUserMenuOpen(false); navigate('/payments'); }}>Payments</button>
                      </li>
                      <li>
                        <button className="w-full text-left px-4 py-3 font-body text-gray-700 hover:bg-gray-50 rounded-xl" onClick={handleLogout}>Sign Out</button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout; 